import { useEffect, useRef, useState } from "react";
import { useLanguage } from "../state/language";

interface Props {
  durationSec: number;
  onDone: () => void;
}

const REACTION_GLYPHS = ["👏", "🔥", "😂", "🎉", "✨"];

/**
 * Group-reaction stage that fills the gap between consuming a card and
 * picking the next player. Host taps "Далі →" to advance whenever the
 * room is done. We deliberately do NOT auto-advance — group reactions
 * vary wildly in length and a 5-second hard cutoff was breaking the
 * party energy. The countdown is now a forward-counting visual hint
 * ("реагуйте уже 3s") that doesn't gate the flow.
 */
export function ReactionStage({ durationSec, onDone }: Props) {
  const { t } = useLanguage();
  const [elapsed, setElapsed] = useState(0);
  const doneRef = useRef(false);

  function finish() {
    if (doneRef.current) return;
    doneRef.current = true;
    onDone();
  }

  useEffect(() => {
    doneRef.current = false;
    setElapsed(0);
    const start = performance.now();
    let raf = 0;
    function step(now: number) {
      setElapsed((now - start) / 1000);
      raf = requestAnimationFrame(step);
    }
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [durationSec]);

  // Progress bar visualises the original reactionWindowSec hint, but
  // saturates at 100% — host can wait as long as they want.
  const pct = durationSec > 0 ? Math.min(1, elapsed / durationSec) : 1;

  return (
    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center px-6 overflow-hidden">
      {REACTION_GLYPHS.map((g, i) => (
        <span
          key={i}
          className="absolute text-5xl pointer-events-none select-none"
          style={{
            top: `${20 + (i * 13) % 65}%`,
            left: `${10 + (i * 19) % 80}%`,
            animation: `pp-react-float ${3 + (i % 3)}s ease-in-out ${i * 200}ms infinite`,
            opacity: 0.7,
            filter: "drop-shadow(0 0 12px rgba(255,255,255,0.3))",
          }}
        >
          {g}
        </span>
      ))}
      <div
        className="font-display text-5xl font-black uppercase relative animate-phase-pop"
        style={{
          color: "#fff",
          textShadow:
            "0 0 18px var(--accent, #ff4ed8), 0 0 36px var(--accent, #ff4ed8)",
        }}
      >
        {t("reaction_title")}
      </div>
      <div className="opacity-70 mt-3 text-sm tracking-wider relative">
        {t("reaction_hint")}
      </div>
      {/* HERO advance button — only way out of this stage. */}
      <button
        type="button"
        onClick={finish}
        className="relative mt-8 px-10 py-4 rounded-full font-display font-black text-2xl uppercase tracking-wide bg-white text-black active:scale-95 transition pp-pulse-cta"
        style={{
          boxShadow:
            "0 0 28px var(--accent, rgba(255,255,255,0.7)), 0 0 56px var(--accent, rgba(255,255,255,0.4))",
        }}
      >
        {t("reaction_continue")} →
      </button>
      {/* Forward-counting elapsed hint — non-blocking. */}
      <div className="absolute bottom-12 left-8 right-8 flex flex-col items-center gap-2">
        <div className="text-[10px] uppercase tracking-wider opacity-50 tabular-nums">
          {Math.floor(elapsed)}s
        </div>
        <div className="h-1 w-full bg-white/15 rounded-full overflow-hidden">
          <div
            className="h-full"
            style={{
              width: `${pct * 100}%`,
              background: "var(--accent, #fff)",
              transition: "width 80ms linear",
              boxShadow: "0 0 12px var(--accent, rgba(255,255,255,0.6))",
            }}
          />
        </div>
      </div>
    </div>
  );
}
